import type { DashboardWidget } from "@/features/widgets/types";
import { widgetCapabilities } from "@/features/widgets/capabilities";
import { inferSignalRole } from "./defaults";
import type { SignalConfig } from "./types";

/**
 * `SignalConfig` descreve a IDENTIDADE do sinal FÍSICO (rótulo, unidade,
 * escala) compartilhada por todos os widgets da mesma fonte `G{slot}_P{port}_
 * {s|acc|v}`. Widgets DERIVADOS (disponibilidade/utilização) expressam esse
 * sinal como uma PORCENTAGEM (0–100 %): a unidade "%" e a escala 0–100
 * pertencem à VISÃO, não ao sinal.
 *
 * Como `useSignalConfigs.upsert` SUBSTITUI a config inteira da fonte, deixar um
 * widget derivado escrever a config fazia o "%" (e a escala 0–100) sobrescrever
 * a unidade real — foi assim que "Distancia acumulada" (acc, em metros) passou
 * a exibir "52.004 %" e "15 %/min" no card de produção da mesma porta. Só os
 * widgets cuja unidade É a unidade do sinal podem definir essa identidade.
 */
export function widgetDefinesSignalIdentity(widget: DashboardWidget): boolean {
  return widgetCapabilities(widget.widgetType).definesSignalIdentity;
}

/**
 * `mode` (DI/CT/PM/HSC/AIN/MB) nao cabe no widget — nao e identidade de
 * exibicao —, entao vem de fora, do `/meta` que a pagina ja tem em maos. Sem
 * ele o papel do sinal degrada: `inferSignalRole` nao consegue distinguir
 * "direction" de "state" nem "flow" de "speed", e salvar um card sobre um sinal
 * HSC ou PM apagaria o papel correto que os defaults haviam inferido.
 *
 * `anterior` e a config que ja existia para esta fonte. Ela entra pela mesma
 * razao do `mode`: ha um fato que o widget nao carrega — se o NOME do sinal foi
 * escolhido por alguem — e sem esse fato a gravacao apaga a resposta. Ver
 * `resolverEscolhaDoNome`.
 */
export function widgetToSignalConfig(
  widget: DashboardWidget,
  mode?: string,
  anterior?: SignalConfig,
): SignalConfig {
  // Source no formato `G{slot}_P{port}_{s|acc|v}` — pelas regras de fonte
  // do modal, só chega aqui se for um sinal real.
  const tag = /^G(\d+)_P(\d+)_(s|acc|v)$/i.exec(widget.source);
  const signal = (tag?.[3] as "s" | "acc" | "v" | undefined) ?? "acc";
  return {
    source: widget.source,
    machineId: widget.machineId,
    label: widget.title,
    labelFromOperator: resolverEscolhaDoNome(widget, anterior),
    signal,
    kind: widget.settings.signalKind,
    role: inferSignalRole(signal, String(mode ?? "").toUpperCase(), widget.settings.signalKind),
    unit: widget.settings.unit,
    activeWhen: widget.settings.activeWhen,
    stateLabels: widget.settings.stateLabels,
    scale:
      widget.settings.min !== undefined &&
      widget.settings.max !== undefined &&
      widget.settings.decimals !== undefined
        ? {
            min: widget.settings.min,
            max: widget.settings.max,
            decimals: widget.settings.decimals,
          }
        : undefined,
    preferredWidget: widget.widgetType,
    size: widget.layout.size === "md" ? undefined : widget.layout.size,
    thresholds: widget.settings.thresholds,
  };
}

/**
 * O nome deste sinal foi ESCOLHIDO por alguem?
 *
 * `renamedByOperator` mora no widget e e a prova direta. Mas ele e novo, e a
 * escolha tambem pode estar registrada so na `SignalConfig` — em `labelFromOperator`,
 * gravado antes do campo existir no widget. Um widget legado carrega o titulo
 * escolhido sem carregar a prova de que foi escolhido.
 *
 * Derivar a resposta APENAS do widget rebaixava essa escolha em silencio.
 * Observado em bancada: abrir um card para mexer na SECAO e salvar apagava o
 * `labelFromOperator` do sinal, e o nome dado pelo operador ("TITULO DO
 * OPERADOR") era substituido pelo nome declarado pelo dispositivo em TODOS os
 * cards daquela fonte — sem ninguem ter tocado no campo de nome.
 *
 * A regra e nao rebaixar sem prova contra: enquanto o titulo continuar sendo o
 * mesmo que a config ja tinha como escolhido, a escolha permanece de pe.
 */
function resolverEscolhaDoNome(
  widget: DashboardWidget,
  anterior?: SignalConfig,
): boolean | undefined {
  if (widget.settings.renamedByOperator) return true;
  if (anterior?.labelFromOperator && anterior.label === widget.title) return true;
  return widget.settings.renamedByOperator;
}
